import { Box, type SxProps, type Theme, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import type { ReactElement, ReactNode } from "react";
import { FONT_SIZE, MOTION, NEUTRAL } from "../../../../theme/tokens";
import { SystemTabBadgeIndicator } from "../../Badges/SystemTabBadgeIndicator";
import type { SystemTabBadge } from "../../Clickable/TabsAndChips/SystemTabs/utils";

/** One navigable destination in the rail. Labels arrive already-translated. */
export type SideRailItem<T> = {
	/** Stable value used for selection + the `?tab=` sync. */
	value: T;
	/** Already-translated label shown beside the icon (and as `aria-label` in the compact rail). */
	label: string;
	/** Leading glyph — sized by the rail, so pass an unstyled icon element. */
	icon: ReactElement;
	disabled?: boolean;
	/** Optional trailing count-pill / status-dot (same shape as `SystemTabs`). */
	badge?: SystemTabBadge;
	/** Per-item test id (falls back to `{dataTestId}-item-{value}` when omitted). */
	dataTestId?: string;
};

type Props<T> = {
	items: SideRailItem<T>[];
	activeValue: T;
	onChange: (next: T) => void;
	/** Optional heading rendered above the item list (a brand tile, title, etc.). */
	header?: ReactNode;
	/** Optional footer pinned to the bottom of the rail (help link, secondary action). */
	footer?: ReactNode;
	/** Expanded rail width (px). Icons-only compact width is derived. Default 232. */
	width?: number;
	/**
	 * Force the compact (icon-only) rail regardless of viewport. When omitted the rail
	 * auto-compacts below the `md` breakpoint so it never crowds a narrow content pane.
	 */
	compact?: boolean;
	/** Accessible label for the `<nav>` landmark. */
	ariaLabel?: string;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

const COMPACT_WIDTH = 68;
const INDICATOR_LAYOUT_ID = "system-side-rail-active";

/**
 * `SystemSideRail` — a generic, persistent vertical navigation rail (icon + label
 * destinations with an animated active indicator). The app-standard left-rail
 * pattern (`design.md` → "persistent left sidebar"): reach for this instead of a
 * bespoke vertical `SystemTabs`/`ToggleButtonGroup`.
 *
 * Generic over the selection value `T`, controlled (`activeValue` + `onChange`), and
 * theme-tokenized. Auto-compacts to an icon-only rail below `md` (override with
 * `compact`). The active-item highlight slides between destinations via a shared
 * Framer `layoutId`; reduced-motion drops the slide (the tinted state remains).
 * Renders semantic `<nav>` + `<button>`s with `aria-current="page"` on the active item.
 */
export const SystemSideRail = <T,>({
	items,
	activeValue,
	onChange,
	header,
	footer,
	width = 232,
	compact,
	ariaLabel,
	sx,
	dataTestId,
}: Props<T>): ReactElement => {
	const theme = useTheme();
	const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
	const isCompact = compact ?? isNarrow;
	const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
	const railWidth = isCompact ? COMPACT_WIDTH : width;
	const accent = theme.palette.primary.main;

	return (
		<Box
			component="nav"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			sx={[
				{
					flexShrink: 0,
					width: railWidth,
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					p: 1,
					borderRight: "1px solid",
					borderColor: "divider",
					bgcolor: NEUTRAL[50],
					// Deliberate breakpoint-only layout transition (rail expand/compact) — a sanctioned exception
					// to "animate transform/opacity only", per the council's open-question resolution: width here
					// is a rare, low-frequency responsive change, not a per-interaction animation.
					transition: `width ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{header && <Box sx={{ px: isCompact ? 0 : 0.5, py: 1 }}>{header}</Box>}
			<Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 0.5, flexGrow: 1 }}>
				{items.map((item) => {
					const selected = item.value === activeValue;
					const itemTestId = item.dataTestId ?? (dataTestId ? `${dataTestId}-item-${String(item.value)}` : undefined);
					return (
						<Box component="li" key={String(item.value)} sx={{ position: "relative" }}>
							{/* Sliding active-highlight — a single shared layoutId means it animates
							    from the previously-active item to this one. Sits behind the button. */}
							{selected && (
								<Box
									component={motion.div}
									layoutId={reduceMotion ? undefined : INDICATOR_LAYOUT_ID}
									transition={{ type: "spring", stiffness: 520, damping: 40 }}
									sx={{
										position: "absolute",
										inset: 0,
										borderRadius: 2,
										bgcolor: alpha(accent, 0.12),
										border: "1px solid",
										borderColor: alpha(accent, 0.24),
										// Left accent bar reinforces the active read even in the compact rail.
										"&::before": {
											content: '""',
											position: "absolute",
											left: 0,
											top: "50%",
											transform: "translateY(-50%)",
											width: 3,
											height: "60%",
											borderRadius: 3,
											backgroundColor: accent,
										},
									}}
								/>
							)}
							<Box
								component="button"
								type="button"
								disabled={item.disabled}
								aria-current={selected ? "page" : undefined}
								aria-label={isCompact ? item.label : undefined}
								title={isCompact ? item.label : undefined}
								data-testid={itemTestId}
								onClick={() => !item.disabled && onChange(item.value)}
								sx={{
									position: "relative",
									width: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: isCompact ? "center" : "flex-start",
									gap: 1.25,
									px: isCompact ? 0 : 1.25,
									py: 1,
									minHeight: 44,
									border: "none",
									borderRadius: 2,
									bgcolor: "transparent",
									color: selected ? accent : "text.secondary",
									font: "inherit",
									fontSize: FONT_SIZE.EMPHASIS,
									fontWeight: selected ? 700 : 500,
									textAlign: "left",
									cursor: item.disabled ? "not-allowed" : "pointer",
									opacity: item.disabled ? 0.5 : 1,
									transition: `color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, background-color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
									// Hover wash only on real pointers, and never over the active tint.
									"@media (hover: hover) and (pointer: fine)": {
										"&:hover": { bgcolor: selected ? "transparent" : alpha(accent, 0.05), color: selected ? accent : "text.primary" },
									},
									"&:active": { transform: "scale(0.98)" },
									"&:focus-visible": { outline: `2px solid ${alpha(accent, 0.4)}`, outlineOffset: 2 },
									"& svg": { fontSize: 20, flexShrink: 0 },
								}}
							>
								{item.icon}
								{!isCompact && (
									<>
										<Box component="span" sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											{item.label}
										</Box>
										{item.badge && <SystemTabBadgeIndicator badge={item.badge} />}
									</>
								)}
								{/* Compact rail: keep the badge as a corner dot so counts/attention still register. */}
								{isCompact && item.badge && (
									<Box sx={{ position: "absolute", top: 6, right: 8 }}>
										<SystemTabBadgeIndicator badge={item.badge.dot ? item.badge : { ...item.badge, dot: true }} />
									</Box>
								)}
							</Box>
						</Box>
					);
				})}
			</Box>
			{footer && <Box sx={{ mt: "auto", px: isCompact ? 0 : 0.5, pt: 1 }}>{footer}</Box>}
		</Box>
	);
};
